import { createContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

// COMPONENTS
import AgreementModal from 'components/Modals/AgreementCreation';
import { RecordOf } from 'immutable';
import { Customer, Vehicle } from 'services/resources';
import { AgreementStatus } from 'constants/agreements';

interface ButtonOptions {
  redirect: boolean;
  status: AgreementStatus;
}

interface Values {
  description: string;
  onPrimaryCallback?: (agreementId?: number) => Promise<void> | void;
  onSuccessCallback?: (agreementId?: number) => Promise<void> | void;
  vehicle?: RecordOf<Vehicle>;
  customerReadOnly?: boolean;
  preselectedCustomer?: Customer;
  preselectedDateOut?: Date;
  preselectedDateIn?: Date;
  primaryButtonClickOptions?: ButtonOptions;
  primaryButtonLabel: string;
  successButtonClickOptions?: ButtonOptions;
  successButtonLabel: string;
}

interface State {
  isShown: boolean;
  values: Values;
  openModal: (props?: Values) => void;
}

type Context = Pick<State, 'openModal'>;

export const GlobalAgreementModalContext = createContext<Context>({
  openModal: (): void => {
    throw Error('No provider has been provided in the upper scope.');
  },
});

const GlobalAgreementModal = ({ children }) => {
  const { formatMessage } = useIntl();

  const defaultState = {
    description: formatMessage({
      id: 'agreementModal.vehicle.description',
    }),
    onPrimaryCallback: undefined,
    onSuccessCallback: undefined,
    vehicle: undefined,
    customerReadOnly: false,
    preselectedCustomer: undefined,
    preselectedDateOut: undefined,
    preselectedDateIn: undefined,
    primaryButtonClickOptions: undefined,
    primaryButtonLabel: formatMessage({
      id: 'agreementModal.vehicle.primary',
    }),
    successButtonClickOptions: undefined,
    successButtonLabel: formatMessage({
      id: 'agreementModal.vehicle.success',
    }),
  };

  const [modalState, setModalState] = useState<State>({
    isShown: false,
    values: defaultState,
    openModal: () => {
      throw Error('No provider has been provided in the upper scope.');
    },
  });

  const showModal = (bool: boolean) => setModalState((prev) => ({ ...prev, isShown: bool }));
  const setValues = (values: State['values']) =>
    setModalState((prev) => ({
      ...prev,
      values: {
        ...values,
      },
    }));

  useEffect(() => {
    setModalState((prev) => {
      return {
        ...prev,
        openModal: (props) => {
          let { preselectedDateOut } = props;

          if (preselectedDateOut > new Date()) {
            // Ensure we are always either 8AM for future days
            preselectedDateOut.setHours(8);
          } else if (preselectedDateOut < new Date()) {
            preselectedDateOut = new Date();
          }

          setValues({
            ...props,
            preselectedDateOut,
          });
          showModal(true);
        },
      };
    });
  }, []);

  return (
    <GlobalAgreementModalContext.Provider value={{ openModal: modalState.openModal }}>
      {children}
      {modalState.isShown && (
        <AgreementModal
          onSuccessCallback={modalState.values.onSuccessCallback}
          onPrimaryCallback={modalState.values.onPrimaryCallback}
          customerReadOnly={modalState.values.customerReadOnly}
          description={modalState.values.description}
          preselectedCustomer={modalState.values.preselectedCustomer}
          preselectedDateOut={modalState.values.preselectedDateOut}
          preselectedDateIn={modalState.values.preselectedDateIn}
          primaryButtonClickOptions={modalState.values.primaryButtonClickOptions}
          primaryButtonLabel={modalState.values.primaryButtonLabel ?? ''}
          successButtonClickOptions={modalState.values.successButtonClickOptions}
          successButtonLabel={modalState.values.successButtonLabel ?? ''}
          isOpen={modalState.isShown}
          onOpen={() => showModal(true)}
          onClose={() => {
            showModal(false);
            setValues(defaultState);
          }}
          vehicle={modalState.values.vehicle}
        />
      )}
    </GlobalAgreementModalContext.Provider>
  );
};

export default GlobalAgreementModal;
